(() => {
  let activeVideo = null;
  let activePlayer = null;
  const warmedSources = new Set();
  const warmTimers = [];
  const warmLinks = [];
  let userRequestedPlayback = false;

  const cancelWarmQueue = () => {
    while (warmTimers.length) {
      window.clearTimeout(warmTimers.pop());
    }

    while (warmLinks.length) {
      warmLinks.pop().remove();
    }
  };

  const stopActiveVideo = (nextPlayer) => {
    if (!activeVideo || activePlayer === nextPlayer) return;

    activeVideo.pause();
    activeVideo.currentTime = 0;
    activePlayer?.classList.remove('is-playing', 'is-loading');
  };

  const showVideoFrame = (video, player) => {
    if (activeVideo !== video || video.paused) return;

    const reveal = () => {
      if (activeVideo !== video || video.paused) return;
      player.classList.remove('is-loading');
      player.classList.add('is-playing');
    };

    if (video.readyState >= 2) {
      reveal();
    }

    if ('requestVideoFrameCallback' in video) {
      video.requestVideoFrameCallback(reveal);
    }

    video.addEventListener('loadeddata', reveal, { once: true });
    video.addEventListener('canplay', reveal, { once: true });
    video.addEventListener('timeupdate', reveal, { once: true });
    window.setTimeout(reveal, 450);
  };

  const ensureVideo = (player) => {
    const existingVideo = player.querySelector('video');
    if (existingVideo) return existingVideo;

    const video = document.createElement('video');
    video.poster = player.dataset.posterSrc || '';
    video.playsInline = true;
    video.preload = 'auto';
    video.fetchPriority = 'high';
    video.disablePictureInPicture = true;
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
    video.addEventListener('contextmenu', (event) => event.preventDefault());
    video.addEventListener('canplay', () => {
      showVideoFrame(video, player);
      if (activeVideo !== video || video.paused === false) return;
      video.play().catch(() => {
        player.classList.remove('is-playing', 'is-loading');
      });
    });
    video.addEventListener('playing', () => {
      showVideoFrame(video, player);
    });
    video.addEventListener('timeupdate', () => {
      showVideoFrame(video, player);
    }, { once: true });
    video.addEventListener('waiting', () => {
      if (activeVideo === video) player.classList.add('is-loading');
    });
    video.addEventListener('pause', () => {
      if (activeVideo === video) player.classList.remove('is-playing', 'is-loading');
    });
    video.addEventListener('ended', () => {
      player.classList.remove('is-playing', 'is-loading');
      activeVideo = null;
      activePlayer = null;
    });

    player.appendChild(video);

    return video;
  };

  const attachSource = (video, player, priority = 'high') => {
    video.fetchPriority = priority;

    if (video.dataset.loaded) return;

    const source = document.createElement('source');
    source.src = player.dataset.videoSrc;
    source.type = 'video/mp4';
    video.appendChild(source);
    video.dataset.loaded = 'true';
    video.load();
  };

  const warmPlayer = (player) => {
    if (userRequestedPlayback) return;

    const src = player.dataset.videoSrc;
    if (!src || warmedSources.has(src)) return;

    warmedSources.add(src);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'video';
    link.href = src;
    link.fetchPriority = 'low';
    document.head.appendChild(link);
    warmLinks.push(link);
  };

  const primePlayer = (player) => {
    userRequestedPlayback = true;
    cancelWarmQueue();

    const video = ensureVideo(player);
    attachSource(video, player, 'high');
  };

  const playPlayer = (player) => {
    userRequestedPlayback = true;
    cancelWarmQueue();
    stopActiveVideo(player);

    const video = ensureVideo(player);

    if (video.paused === false && player.classList.contains('is-playing')) {
      video.pause();
      activeVideo = null;
      activePlayer = null;
      return;
    }

    if (activeVideo === video && player.classList.contains('is-loading')) return;

    activeVideo = video;
    activePlayer = player;
    video.fetchPriority = 'high';
    player.classList.add('is-loading');
    attachSource(video, player, 'high');

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        player.classList.remove('is-playing', 'is-loading');
        activeVideo = null;
        activePlayer = null;
      });
    }
  };

  const warmAfterPageLoad = () => {
    const players = [...document.querySelectorAll('.daochant-lazy-video-player[data-preload-after-page="true"]')];
    const uniquePlayers = players.filter((player, index, list) => {
      return list.findIndex((item) => item.dataset.videoSrc === player.dataset.videoSrc) === index;
    });

    const scheduleWarm = () => {
      if (userRequestedPlayback) return;

      uniquePlayers.forEach((player, index) => {
        const timer = window.setTimeout(() => warmPlayer(player), index * 1800);
        warmTimers.push(timer);
      });
    };

    const scheduleWarmAfterVisuals = () => {
      const delay = window.matchMedia('(max-width: 749px)').matches ? 3200 : 1800;
      const timer = window.setTimeout(scheduleWarm, delay);
      warmTimers.push(timer);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(scheduleWarmAfterVisuals, { timeout: 5000 });
      return;
    }

    const timer = window.setTimeout(scheduleWarmAfterVisuals, 1800);
    warmTimers.push(timer);
  };

  document.addEventListener('pointerdown', (event) => {
    const player = event.target.closest('.daochant-lazy-video-player');
    if (!player) return;

    primePlayer(player);
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const player = event.target.closest('.daochant-lazy-video-player');
    if (!player) return;

    playPlayer(player);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const player = event.target.closest('.daochant-lazy-video-player');
    if (!player) return;

    event.preventDefault();
    playPlayer(player);
  });

  if (document.readyState === 'complete') {
    warmAfterPageLoad();
  } else {
    window.addEventListener('load', warmAfterPageLoad, { once: true });
  }
})();
